import { ArrowUpDown, Columns, Download, Printer, Search, SlidersHorizontal, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { TypeSortDirection } from '@/libs/interfaces'

export interface FilterOption {
  key: string
  label: string
  type: 'select' | 'text' | 'date'
  options?: { value: string; label: string }[]
}

export interface SortOption {
  key: string
  label: string
  direction?: TypeSortDirection
}

export interface ColumnConfig {
  key: string
  label: string
  visible: boolean
}

interface ListFiltersProps {
  searchPlaceholder?: string
  searchValue: string
  onSearchChange: (value: string) => void
  filters?: FilterOption[]
  activeFilters?: Record<string, string>
  onFilterChange?: (key: string, value: string) => void
  onClearFilters?: () => void
  sortOptions?: SortOption[]
  currentSort?: string
  sortDirection?: TypeSortDirection
  onSortChange?: (key: string, direction: TypeSortDirection) => void
  columns?: ColumnConfig[]
  onColumnToggle?: (key: string) => void
  onExport?: (format: 'csv' | 'xlsx' | 'pdf') => void
  onPrint?: () => void
  showExport?: boolean
  showPrint?: boolean
  showColumnToggle?: boolean
  hideSearch?: boolean
}

export function ListFilters({
  searchPlaceholder,
  searchValue,
  onSearchChange,
  filters = [],
  activeFilters = {},
  onFilterChange,
  onClearFilters,
  sortOptions = [],
  currentSort = sortOptions[0]?.key,
  sortDirection = 'asc',
  onSortChange,
  columns = [],
  onColumnToggle,
  onExport,
  onPrint,
  showExport = true,
  showPrint = true,
  showColumnToggle = true,
  hideSearch = false,
}: ListFiltersProps) {
  const { t } = useTranslation()
  const [filtersOpen, setFiltersOpen] = useState(false)

  const activeFilterCount = Object.values(activeFilters).filter((v) => v && v !== 'all').length

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      {!hideSearch && (
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder || t('common.search')}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
          {searchValue && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => onSearchChange('')}
                  aria-label={t('common.clear')}
                >
                  <X className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent collisionPadding={16}>
                {t('tooltips.filters.clearSearch')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {/* Filters */}
        {filters.length > 0 && (
          <Popover open={filtersOpen} onOpenChange={setFiltersOpen}>
            <Tooltip>
              <PopoverTrigger asChild>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="relative"
                    aria-label={t('common.filter')}
                  >
                    <SlidersHorizontal className="w-4 h-4 lg:mr-2" />
                    <span className="hidden lg:inline">{t('common.filter')}</span>
                    {activeFilterCount > 0 && (
                      <span className="ml-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </TooltipTrigger>
              </PopoverTrigger>
              <TooltipContent collisionPadding={16}>{t('tooltips.filters.filter')}</TooltipContent>
            </Tooltip>
            <PopoverContent
              className="w-[min(20rem,calc(100vw-2rem))]"
              align="start"
              sideOffset={4}
              collisionPadding={16}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">{t('filters.title')}</h4>
                  {activeFilterCount > 0 && onClearFilters && (
                    <Button variant="ghost" size="sm" onClick={onClearFilters}>
                      {t('filters.clearAll')}
                    </Button>
                  )}
                </div>
                {filters.map((filter) => (
                  <div key={filter.key} className="space-y-2">
                    <label className="text-sm font-medium">{filter.label}</label>
                    {filter.type === 'select' && filter.options && (
                      <Select
                        value={activeFilters[filter.key] || 'all'}
                        onValueChange={(value) => onFilterChange?.(filter.key, value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('common.all')}</SelectItem>
                          {filter.options.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {filter.type === 'text' && (
                      <Input
                        value={activeFilters[filter.key] || ''}
                        onChange={(e) => onFilterChange?.(filter.key, e.target.value)}
                        placeholder={filter.label}
                      />
                    )}
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Sort */}
        {sortOptions.length > 0 && (
          <DropdownMenu>
            <Tooltip>
              <DropdownMenuTrigger asChild>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" aria-label={t('filters.sort')}>
                    <ArrowUpDown className="w-4 h-4 lg:mr-2" />
                    <span className="hidden lg:inline">{t('filters.sort')}</span>
                  </Button>
                </TooltipTrigger>
              </DropdownMenuTrigger>
              <TooltipContent collisionPadding={16}>{t('tooltips.filters.sort')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('filters.sortBy')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {sortOptions.map((opt) => (
                <DropdownMenuCheckboxItem
                  key={opt.key}
                  checked={currentSort === opt.key}
                  onCheckedChange={() =>
                    onSortChange?.(opt.key, sortDirection === 'asc' ? 'desc' : 'asc')
                  }
                >
                  {opt.label}
                  {currentSort === opt.key && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Column Toggle */}
        {showColumnToggle && columns.length > 0 && (
          <DropdownMenu>
            <Tooltip>
              <DropdownMenuTrigger asChild>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" aria-label={t('filters.columns')}>
                    <Columns className="w-4 h-4 lg:mr-2" />
                    <span className="hidden lg:inline">{t('filters.columns')}</span>
                  </Button>
                </TooltipTrigger>
              </DropdownMenuTrigger>
              <TooltipContent collisionPadding={16}>{t('tooltips.filters.columns')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('filters.toggleColumns')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={col.visible}
                  onCheckedChange={() => onColumnToggle?.(col.key)}
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Export */}
        {showExport && onExport && (
          <DropdownMenu>
            <Tooltip>
              <DropdownMenuTrigger asChild>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" aria-label={t('filters.export')}>
                    <Download className="w-4 h-4 lg:mr-2" />
                    <span className="hidden lg:inline">{t('filters.export')}</span>
                  </Button>
                </TooltipTrigger>
              </DropdownMenuTrigger>
              <TooltipContent collisionPadding={16}>{t('tooltips.filters.export')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem onCheckedChange={() => onExport('csv')}>
                {t('filters.exportCSV')}
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem onCheckedChange={() => onExport('xlsx')}>
                {t('filters.exportExcel')}
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem onCheckedChange={() => onExport('pdf')}>
                {t('filters.exportPDF')}
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Print */}
        {showPrint && onPrint && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={onPrint} aria-label={t('filters.print')}>
                <Printer className="w-4 h-4 lg:mr-2" />
                <span className="hidden lg:inline">{t('filters.print')}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent collisionPadding={16}>{t('tooltips.filters.print')}</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}
